/**
 * <sticky-header> — collapses the header's nav row by scroll direction:
 * hide on scroll-down (past a threshold), reveal on scroll-up. The element
 * only toggles [data-collapsed]; all visuals live in the section's CSS, so
 * the header works fully without JS (nav row simply always visible).
 * Light-DOM, self-guarded, passive listener, rAF-throttled.
 */
if (!customElements.get('sticky-header')) {
  customElements.define(
    'sticky-header',
    class StickyHeader extends HTMLElement {
      connectedCallback() {
        if (!this.hasAttribute('data-collapse')) return;

        this.lastY = window.scrollY;
        this.ticking = false;

        this.onScroll = () => {
          if (this.ticking) return;
          this.ticking = true;
          requestAnimationFrame(() => {
            // Clamp out iOS rubber-band overscroll so the elastic bounce-back
            // never reads as a phantom direction change.
            const maxY = document.documentElement.scrollHeight - window.innerHeight;
            const y = Math.min(Math.max(0, window.scrollY), maxY);
            const delta = y - this.lastY;
            // Re-measured per frame: the header's height changes across
            // breakpoints, orientation and font swap.
            const threshold = this.offsetHeight * 2;
            // Small hysteresis so trackpad jitter doesn't flicker the nav.
            if (Math.abs(delta) > 4) {
              if (delta > 0 && y > threshold) {
                // Never collapse out from under keyboard focus.
                if (!this.contains(document.activeElement)) {
                  this.setAttribute('data-collapsed', '');
                }
              } else if (delta < 0 || y <= threshold) {
                this.removeAttribute('data-collapsed');
              }
              this.lastY = y;
            }
            this.ticking = false;
          });
        };

        window.addEventListener('scroll', this.onScroll, { passive: true });
      }

      disconnectedCallback() {
        if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      }
    }
  );
}
